import { LRUCache } from 'lru-cache';

function hashObject(obj) {
  const normalizedObj = {};
  const keys = Object.keys(obj).sort();
  for (const key of keys) {
    normalizedObj[key] = obj[key];
  }
  const str = JSON.stringify(normalizedObj);
  return str;
}

const cache = new LRUCache<string, object>({
  max: 100,
  ttl: 1000 * 60 * 5,
});

/**
 * cache request
 *
 * The cache is ONE process-wide LRU and the entry key is `keyPrefix + hash(args)`, so keyPrefix is
 * a global identity, not a local label. Two registrations sharing a prefix share the entry: with the
 * same args, whichever call runs first decides what BOTH callers get back. If their fetchers resolve
 * to different shapes the loser gets a value it cannot read, and inside a yup test that throw
 * abandons the whole validation pass (SC-1323). So: one prefix, one owner, one shape - when two
 * places need the same data, export the helper rather than re-declaring the prefix.
 *
 * @param keyPrefix globally unique key prefix (see above)
 * @param fetcher request function (return Promise)
 * @param ttl single cache expiration time (ms)
 */
export function requestWithCache<T extends (...args: any[]) => Promise<any>>(
  keyPrefix: string,
  fetcher: T,
  ttl?: number,
) {
  return (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    const key = [keyPrefix, hashObject(args)].join('_');

    const value = cache.get(key);

    if (value) {
      return value as ReturnType<T>;
    }

    const promise = fetcher(...args);

    cache.set(key, promise, { ttl });

    promise
      .then((result) => {
        if (!result || (typeof result === 'object' && result.success === false)) {
          cache.delete(key);
        }
        return result;
      })
      .catch(() => {
        cache.delete(key);
        // Don't re-throw: this branch is only for cache eviction.
        // The original `promise` returned to callers handles the rejection.
      });

    return promise;
  };
}

/**
 * clear all cache
 */
export function clearAllCache() {
  cache.clear();
}
